import { computed, inject, toRef, type ComputedRef } from 'vue'
import { ComponentTypes, type ComponentNode } from '@canvas-builder/types/canvas'
import useCanvas from '@canvas-builder/composables/useCanvas'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

/**
 * Composable for handling scribble component toolbar positioning
 * Provides reactive toolbar positioning based on component Y position and feature flags
 */
export function useScribbleToolbar(componentId: string) {
  const { componentNodesById, getParentType } = useCanvas()

  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const isScribbleChild = computed(() => {
    return getParentType(toRef(componentId)).value === ComponentTypes.Scribble
  })

  const enableReactiveToolbarPositioning = computed(() => {
    return !!launchDarkly.value.enable_reactive_scribble_component_toolbar
  })

  /**
   * Determines whether a scribble child component should show only the top toolbar.
   * When reactive positioning is disabled, always shows top toolbar for scribble children.
   * When reactive positioning is enabled, calculates based on Y position - components
   * positioned less than 50px from the top should show the bottom toolbar instead.
   */
  const shouldShowOnlyTopToolbar = computed(() => {
    if (!isScribbleChild.value) return false

    // If reactive toolbar positioning is disabled, always show only top toolbar for scribble children
    if (!enableReactiveToolbarPositioning.value) return true

    // For scribble children with reactive positioning enabled, get the y position from the component node in canvas context
    const componentNode = componentNodesById.value[componentId] as ComponentNode
    const topPosition = componentNode?.style?.top
    if (typeof topPosition === 'string') {
      const yPos = parseInt(topPosition)
      // If y position is less than 50px, don't show only top toolbar
      return yPos >= 50
    }

    // Default to showing only top toolbar for scribble children
    return true
  })

  /**
   * Determines whether a scribble child component should show only the bottom toolbar.
   * When reactive positioning is disabled, never shows bottom toolbar (always top).
   * When reactive positioning is enabled, calculates based on Y position - components
   * positioned less than 50px from the top should show only the bottom toolbar for better accessibility.
   */
  const shouldShowOnlyBottomToolbar = computed(() => {
    // If reactive toolbar positioning is disabled, never show only bottom toolbar (always show top)
    if (!isScribbleChild.value || !enableReactiveToolbarPositioning.value) return false

    // For scribble children, get the y position from the component node in canvas context
    const componentNode = componentNodesById.value[componentId] as ComponentNode
    const topPosition = componentNode?.style?.top
    if (typeof topPosition === 'string') {
      const yPos = parseInt(topPosition)
      // If y position is less than 50px, show only bottom toolbar
      return yPos < 50
    }

    // Default to not showing only bottom toolbar
    return false
  })

  return {
    isScribbleChild,
    shouldShowOnlyTopToolbar,
    shouldShowOnlyBottomToolbar,
    enableReactiveToolbarPositioning,
  }
}
